import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/auth";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  IconBook,
  IconBriefcase,
  IconCalendar,
  IconClipboard,
  IconHome,
  IconImage,
  IconInbox,
  IconMegaphone,
  IconPackage,
  IconSettings,
  IconSparkles,
  IconTicket,
  IconTruck,
  IconUsers,
  IconWallet,
} from "@/components/icons";

type NavItem = {
  to: string;
  labelKey: string;
  permission: string;
  icon: (p: { size?: number }) => JSX.Element;
};

/** The sidebar, by group. Every route keeps its own permission gate. */
export const NAV_GROUPS: { key: string; items: NavItem[] }[] = [
  {
    key: "sales",
    items: [
      { to: "/", labelKey: "nav.home", permission: "bookings:read", icon: IconHome },
      { to: "/leads", labelKey: "nav.leads", permission: "leads:read", icon: IconInbox },
      { to: "/bookings", labelKey: "nav.bookings", permission: "bookings:read", icon: IconTicket },
      { to: "/customers", labelKey: "nav.customers", permission: "customers:read", icon: IconUsers },
      { to: "/packages", labelKey: "nav.packages", permission: "packages:read", icon: IconPackage },
    ],
  },
  {
    key: "operations",
    items: [
      { to: "/calendar", labelKey: "nav.calendar", permission: "calendar:read", icon: IconCalendar },
      { to: "/operations", labelKey: "nav.operations", permission: "operations:read", icon: IconTruck },
      { to: "/finance", labelKey: "nav.finance", permission: "finance:read", icon: IconWallet },
      { to: "/agents", labelKey: "nav.agents", permission: "agents:read", icon: IconBriefcase },
    ],
  },
  {
    key: "ai",
    items: [
      { to: "/dispatch", labelKey: "nav.dispatch", permission: "orchestrator:read", icon: IconSparkles },
      { to: "/knowledge", labelKey: "nav.knowledge", permission: "knowledge:read", icon: IconBook },
      { to: "/content", labelKey: "nav.content", permission: "content:read", icon: IconMegaphone },
      { to: "/media", labelKey: "nav.media", permission: "media:read", icon: IconImage },
    ],
  },
  {
    key: "system",
    items: [
      { to: "/audit", labelKey: "nav.audit", permission: "audit:read", icon: IconClipboard },
      { to: "/admin", labelKey: "nav.admin", permission: "users:manage", icon: IconSettings },
    ],
  },
];

/** «Azamat Rakhmanov» -> «AR». */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("") || "?";
}

function Logo() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-accent text-base font-extrabold text-white">
        N
      </span>
      <span className="leading-tight">
        <span className="block text-[15px] font-extrabold text-ink">NovaMoon</span>
        <span className="block text-xs font-medium text-ink-muted">Travel OS</span>
      </span>
    </div>
  );
}

function SidebarContent({ onClose }: { onClose?: () => void }) {
  const { t } = useTranslation();
  const { me, logout, can } = useAuth();
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between px-5 pb-4 pt-5">
        <Logo />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="rounded-md p-1 text-xl leading-none text-ink-muted hover:text-ink"
          >
            ×
          </button>
        )}
      </div>
      <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-4" aria-label={t("nav.menu")}>
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((n) => can(n.permission));
          if (items.length === 0) return null;
          return (
            <div key={group.key} className="mt-4 first:mt-1">
              <div className="px-3 pb-1.5 text-xs font-semibold text-ink-muted">
                {t(`nav.groups.${group.key}`)}
              </div>
              <ul className="space-y-0.5">
                {items.map((n) => (
                  <li key={n.to}>
                    <NavLink
                      to={n.to}
                      end={n.to === "/"}
                      className={({ isActive }) =>
                        "flex items-center gap-2.5 rounded-[10px] px-3 py-2 text-sm transition " +
                        (isActive
                          ? "nm-nav-active bg-accent font-semibold text-white shadow-[0_4px_12px_rgba(30,107,101,0.25)]"
                          : "font-medium text-ink-muted hover:bg-accent-soft hover:text-ink")
                      }
                    >
                      <n.icon size={18} />
                      <span className="truncate">{t(n.labelKey)}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>
      <div className="shrink-0 border-t border-line bg-surface px-4 py-4">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-sm font-bold text-accent"
          >
            {initials(me?.name)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-ink" title={me?.name}>
              {me?.name}
            </div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => logout()}
                className="text-xs font-medium text-ink-muted hover:text-accent"
              >
                {t("nav.signOut")}
              </button>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The signed-in frame: a 248px sidebar on the left (a burger drawer below
 * 900px) and the page on the right. */
export function AppShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // a tap on a menu item in the drawer navigates — close it
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="min-h-screen bg-page">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[248px] border-r border-line bg-surface nav:block">
        <SidebarContent />
      </aside>

      <header
        className="sticky z-20 flex items-center gap-3 border-b border-line bg-surface px-4 py-3 nav:hidden"
        style={{ top: "env(safe-area-inset-top, 0px)" }}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t("nav.openMenu")}
          aria-expanded={open}
          className="flex h-10 w-10 items-center justify-center rounded-[10px] border border-line text-ink"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path d="M2 4.5h14M2 9h14M2 13.5h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
        <Logo />
      </header>

      {open && (
        <div className="fixed inset-0 z-40 nav:hidden" role="dialog" aria-modal="true" aria-label={t("nav.menu")}>
          <div className="absolute inset-0 bg-ink/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[248px] max-w-[85vw] border-r border-line bg-surface shadow-xl">
            <SidebarContent onClose={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <main className="min-w-0 nav:pl-[248px]">
        <div className="mx-auto max-w-6xl px-4 py-6 nav:px-8 nav:py-8">{children}</div>
      </main>
    </div>
  );
}
